<script setup lang="ts">
import { Ref, ref, watch, computed } from 'vue'
import { storeToRefs } from 'pinia'
import GbButton from '@/components/GbButton.vue'
import GbDialog from '@/components/GbDialog.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import { deleteFolder } from './api.ts'
import { useLibraryStore } from './library.store.ts'
import { useUserStore } from './../authentication/user.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { useLibraryFolderAnalytics } from './composables/useLibraryFolderAnalytics'
import { useRouter } from 'vue-router'

const props = defineProps({
  externalOpen: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['update:externalOpen'])
const userStore = useUserStore()
const libraryStore = useLibraryStore()
const router = useRouter()
const analytics = getAnalyticsTracker()
const { getLegacyEventData, getEventData } = useLibraryFolderAnalytics()
const { currentFolder, isCurrentFolderArchived } = storeToRefs(libraryStore)

const folderName = computed(() => {
  return currentFolder.value.name
})

const visibleRef: Ref<boolean> = ref(props.externalOpen)
const deleteError: Ref<boolean> = ref(false)

const closeModal = function (): void {
  visibleRef.value = false
  emit('update:externalOpen', false)
}

watch(
  () => props.externalOpen,
  () => {
    visibleRef.value = props.externalOpen
  }
)

watch(
  () => visibleRef.value,
  () => {
    emit('update:externalOpen', visibleRef.value)
  }
)

const sendDeleteFolder = async function () {
  deleteError.value = false
  try {
    await deleteFolder({
      userUuid: userStore.userId,
      folderUuid: currentFolder.value.uuid,
    })
    router.push({ name: 'AllFolders' })
    analytics.sendEvent('Deleted Student', getLegacyEventData.value) // to be deprecated
    analytics.sendEvent('Deleted Folder', getEventData.value)
    if (isCurrentFolderArchived.value) {
      libraryStore.fetchArchivedFolders(userStore.userId)
    } else {
      libraryStore.fetchFolders(userStore.userId)
    }
    closeModal()
    deleteError.value = false
  } catch (e) {
    deleteError.value = true
    console.error(e)
  }
}
</script>
<template>
  <GbDialog
    dialogId="delete-folder-modal"
    v-model:visible="visibleRef"
    header="Delete Folder"
    :footer-buttons="true"
  >
    <template #bodyContent>
      <div class="flex flex-col gap-2">
        <p v-if="isCurrentFolderArchived" class="fs-exclude">
          Are you sure you want to delete the folder "{{ folderName }}"? You will lose all resources associated with the folder. This action is irreversible.
        </p>
        <p v-else class="fs-exclude">
          Are you sure you want to delete the folder "{{ folderName }}"? You will lose all resources associated with the folder and any shared links will become invalid. Others will not be able to access the folder unless they have copied the folder to their library. This action is irreversible.
        </p>
        <GbInlineMessage gbTheme="red" v-if="deleteError"
          >We couldn't delete the folder. Try again later.</GbInlineMessage
        >
      </div>
    </template>
    <template #footerButtonLeft>
      <GbButton
        type="button"
        gbTheme="grayOutlineSecondary"
        label="Cancel"
        @click="closeModal"
      ></GbButton>
    </template>
    <template #footerButtonRight>
      <GbButton type="button" gbTheme="redPrimary" label="Delete" @click="sendDeleteFolder"></GbButton>
    </template>
  </GbDialog>
</template>
